import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { captureException } from '@sentry/react';
import { SUPPORTED_LANGUAGES, SupportedLanguage, LANGUAGE_NAMES } from '../i18n';
import { getTenantId } from '../api/apiHelpers';
// This provider mounts on the public /embed/* routes, where the page may be
// iframed and localStorage access THROWS rather than returning null. See
// @mediatechgroup/app-version's safe storage helpers.
import { safeGetItem, safeSetItem } from '@mediatechgroup/app-version';

interface LanguageContextType {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  supportedLanguages: readonly SupportedLanguage[];
  languageNames: Record<SupportedLanguage, string>;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: React.ReactNode;
}

// Helper function to get user's preferred language from stored user details
function getStoredUserPreferredLanguage(): SupportedLanguage | null {
  try {
    const userDetails = safeGetItem('userDetails');
    if (userDetails) {
      const user = JSON.parse(userDetails);
      const preferred = user?.preferred_language as SupportedLanguage;
      if (preferred && SUPPORTED_LANGUAGES.includes(preferred)) {
        return preferred;
      }
    }
  } catch (e) {
    captureException(e);
  }
  return null;
}

// Helper function to fetch user's preferred language from the database
async function fetchUserLanguagePreference(token: string, userId: string): Promise<SupportedLanguage | null> {
  const tenantId = getTenantId();
  const authSource = safeGetItem('authSource');

  if (!tenantId) return null;

  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/users/${userId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-tenant-id': tenantId,
        'x-auth-source': authSource || 'tenant',
      },
    });

    if (response.ok) {
      const data = await response.json();
      const preferred = data?.user?.preferred_language as SupportedLanguage;
      if (preferred && SUPPORTED_LANGUAGES.includes(preferred)) {
        return preferred;
      }
    }
  } catch (error) {
    captureException(error);
  }
  return null;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();

  const [language, setLanguageState] = useState<SupportedLanguage>(() => {
    // Priority 1: Explicit user choice saved on this device
    const stored = safeGetItem('language') as SupportedLanguage;
    if (stored && SUPPORTED_LANGUAGES.includes(stored)) {
      return stored;
    }

    // Priority 2: User's saved preference from their profile (synced from database on login)
    const userPreferred = getStoredUserPreferredLanguage();
    if (userPreferred) {
      // Also cache to localStorage for faster access next time
      safeSetItem('language', userPreferred);
      return userPreferred;
    }

    // Priority 3: Browser language detection
    const detected = i18n.language?.split('-')[0] as SupportedLanguage;
    return SUPPORTED_LANGUAGES.includes(detected) ? detected : 'en';
  });

  const setLanguage = useCallback((lang: SupportedLanguage) => {
    if (SUPPORTED_LANGUAGES.includes(lang)) {
      setLanguageState(lang);
      safeSetItem('language', lang);
      i18n.changeLanguage(lang);
      document.documentElement.lang = lang;

      // Sync to backend if user is authenticated
      const token = safeGetItem('authToken');
      const userDetailsStr = safeGetItem('userDetails');
      if (token && userDetailsStr) {
        try {
          const user = JSON.parse(userDetailsStr);
          if (user?.id) {
            // Update userDetails in localStorage to keep it in sync
            user.preferred_language = lang;
            safeSetItem('userDetails', JSON.stringify(user));

            // Fire and forget - don't block UI on backend sync
            updateUserLanguagePreference(token, user.id, lang).catch(captureException);
          }
        } catch (e) {
          captureException(e);
        }
      }
    }
  }, [i18n]);

  // Fetch user's language preference from database on mount if authenticated
  // This handles the case where localStorage was cleared but user is still logged in
  useEffect(() => {
    const syncLanguageFromDatabase = async () => {
      const storedLanguage = safeGetItem('language');
      // Only fetch from database if no local preference is set
      if (storedLanguage) return;

      const token = safeGetItem('authToken');
      const userDetails = safeGetItem('userDetails');

      if (token && userDetails) {
        try {
          const user = JSON.parse(userDetails);
          if (user?.id) {
            const dbLanguage = await fetchUserLanguagePreference(token, user.id);
            if (dbLanguage && dbLanguage !== language) {
              setLanguageState(dbLanguage);
              safeSetItem('language', dbLanguage);
              i18n.changeLanguage(dbLanguage);
              document.documentElement.lang = dbLanguage;
            }
          }
        } catch (e) {
          captureException(e);
        }
      }
    };

    syncLanguageFromDatabase();
    // This effect intentionally runs only on mount to sync from database once
  }, []);

  // Sync i18n language on mount and when language changes
  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
    document.documentElement.lang = language;
  }, [language, i18n]);

  const value: LanguageContextType = {
    language,
    setLanguage,
    supportedLanguages: SUPPORTED_LANGUAGES,
    languageNames: LANGUAGE_NAMES,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Helper function to sync language preference to backend
async function updateUserLanguagePreference(token: string, userId: string, language: string): Promise<void> {
  const tenantId = getTenantId();
  const authSource = safeGetItem('authSource');

  if (!tenantId) return;

  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/users/${userId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-tenant-id': tenantId,
        'x-auth-source': authSource || 'tenant',
      },
      body: JSON.stringify({ preferred_language: language }),
    });

    if (!response.ok) {
      captureException(new Error(`Failed to sync language preference: ${response.status}`));
    }
  } catch (error) {
    captureException(error);
  }
}

export default LanguageContext;
